import { sharedResources } from '../resources/sharedResources';
import { deepMerge } from './mergeI18nResources';
function camelCaseToSnakeCase(string) {
    return string
        .replace(/([a-z])([A-Z])/g, '$1_$2') // insert _ between camelCase boundaries
        .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2') // handle ALLCAPS → all_caps
        .toLowerCase();
}
// Lazy glob: each translation file becomes its own dynamic import, so a language
// is only fetched when it is actually used (active language at boot, the rest on
// switch). `manualChunks` in the app vite config groups one language's files into
// a single chunk, so loading a language is one request, not one per namespace.
const namespaceModules = import.meta.glob('@translations/**/*.json');
// language -> namespace (snake_case) -> loader
const namespaceLoadersByLanguage = {};
for (const path in namespaceModules) {
    // file locations: @translations/{language}/{namespace}.json
    const [, language, namespace] = path.match(/([^/]+)\/([^/]+)\.json$/);
    (namespaceLoadersByLanguage[language] ??= {})[camelCaseToSnakeCase(namespace)] = namespaceModules[path];
}
async function loadLanguageResourcesUncached(language) {
    const loaders = namespaceLoadersByLanguage[language] ?? {};
    const sharedForLanguage = sharedResources[language] ?? {};
    const entries = await Promise.all(Object.entries(loaders).map(async ([namespace, loadNamespace]) => {
        const appNamespace = (await loadNamespace()).default;
        const sharedNamespace = sharedForLanguage[namespace];
        // Shared, package-owned strings (UI/error/toast/map/privacy-consent labels,
        // months, weekdays) are the base; app resources win on any conflicting key.
        const merged = sharedNamespace ? deepMerge(sharedNamespace, appNamespace) : appNamespace;
        return [namespace, merged];
    }));
    const resources = Object.fromEntries(entries);
    // Shared-only namespaces the app does not redeclare (kept for correctness if
    // shared ever owns a namespace with no matching app file).
    for (const namespace in sharedForLanguage) {
        resources[namespace] ??= sharedForLanguage[namespace];
    }
    return resources;
}
const languageResourcesCache = {};
/**
 * Loads and merges every namespace for a single language. Memoized per language
 * so repeated calls (e.g. one per namespace from i18next) reuse the same work.
 */
export function loadLanguageResources(language) {
    return (languageResourcesCache[language] ??= loadLanguageResourcesUncached(language));
}
